import React, { useEffect, useMemo, useState } from 'react';
import { Area, CartesianGrid, ComposedChart, ReferenceLine, Scatter, XAxis } from 'recharts';
import { motion } from 'motion/react';
import { toast } from 'sonner';
import { Pencil } from 'lucide-react';
import { ChartContainer, ChartTooltip } from './ui/chart';
import LoadingIndicator from './LoadingIndicator';
import { Button as StatefulButton } from './ui/stateful-button';
import { Tabs, TabsContent, TabsContents, TabsList, TabsTrigger } from './animate-ui/components/animate/tabs';
import {
  fetchUserRedemptionCounts,
  fetchUserRedemptions,
  fetchTrackingHistory,
  startTrackingUser,
  TrackingHistoryResponse,
  TrackingSnapshot,
  UserRedemptionCount
} from '../api';
import { Redemption } from '../types';

const COOKIE_NAME = 'eazykee_tracking_username';
const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
const CHART_CONFIG = {
  points: { label: 'Points', color: '#ffffff' },
  projectedPoints: { label: 'Points', color: '#ffffff' }
};

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          theme?: 'dark' | 'light' | 'auto';
          callback?: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
        }
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

function getCookie(name: string) {
  const value = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${name}=`))
    ?.split('=')
    .slice(1)
    .join('=') || '';

  try {
    return decodeURIComponent(value);
  } catch {
    return '';
  }
}

function setCookie(name: string, value: string) {
  document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${60 * 60 * 24 * 365}; path=/; SameSite=Lax`;
}

function formatNumber(value?: number | null) {
  return Number(value || 0).toLocaleString('en-US');
}

function isValidTrackingPoint(snapshot: TrackingSnapshot) {
  const points = Number(snapshot.points);
  return Number.isFinite(points) && points >= 0 && Number.isFinite(new Date(snapshot.captured_at).getTime());
}

function formatMidnightLabel(value: number) {
  return new Date(value).toLocaleTimeString('hu-HU', {
    hour: '2-digit',
    minute: '2-digit'
  });
}

function getCurrentDayMidnightMarker(firstTime: number, latestTime: number) {
  if (!firstTime || !latestTime || latestTime <= firstTime) return null;

  const todayMidnight = new Date();
  todayMidnight.setHours(0, 0, 0, 0);
  const timestamp = todayMidnight.getTime();

  return timestamp >= firstTime && timestamp <= latestTime ? timestamp : null;
}

function getAveragePointsPerHour(history: TrackingSnapshot[]) {
  if (history.length < 2) return 0;

  const sorted = [...history].sort((a, b) => new Date(a.captured_at).getTime() - new Date(b.captured_at).getTime());
  const first = sorted[0];
  const latest = sorted[sorted.length - 1];
  const elapsedHours = (new Date(latest.captured_at).getTime() - new Date(first.captured_at).getTime()) / (60 * 60 * 1000);

  if (elapsedHours <= 0) return 0;
  return Math.max(0, (latest.points - first.points) / elapsedHours);
}

function formatNoPointsDuration(minutes: number) {
  if (minutes < 30) return '';
  if (minutes < 60) return `${Math.floor(minutes)} perce`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours === 1 ? '1 órája' : `${hours} órája`;

  const days = Math.floor(hours / 24);
  return days === 1 ? '1 napja' : `${days} napja`;
}

function getNoPointsMessage(history: TrackingSnapshot[]) {
  if (history.length < 2) return '';

  const sorted = [...history].sort((a, b) => new Date(a.captured_at).getTime() - new Date(b.captured_at).getTime());
  let lastIncreaseAt = '';

  for (let index = sorted.length - 1; index > 0; index--) {
    const currentPoints = Number(sorted[index].points || 0);
    const previousPoints = Number(sorted[index - 1].points || 0);

    if (currentPoints > previousPoints) {
      lastIncreaseAt = sorted[index].captured_at;
      break;
    }
  }

  if (!lastIncreaseAt) {
    lastIncreaseAt = sorted[0].captured_at;
  }

  const minutesSinceIncrease = (Date.now() - new Date(lastIncreaseAt).getTime()) / (60 * 1000);
  const label = formatNoPointsDuration(minutesSinceIncrease);

  return label ? `Nem jött pontod több mint ${label}` : '';
}

function hasThreeDays(history: TrackingSnapshot[]) {
  if (history.length < 2) return false;
  const first = new Date(history[0].captured_at).getTime();
  const latest = new Date(history[history.length - 1].captured_at).getTime();
  return latest - first >= DAY_MS * 3;
}

function hasOneHourOfData(history: TrackingSnapshot[]) {
  if (history.length < 2) return false;
  const sorted = [...history].sort((a, b) => new Date(a.captured_at).getTime() - new Date(b.captured_at).getTime());
  const first = new Date(sorted[0].captured_at).getTime();
  const latest = new Date(sorted[sorted.length - 1].captured_at).getTime();
  return latest - first >= HOUR_MS;
}

function interpolatePointsAt(history: TrackingSnapshot[], timestamp: string) {
  if (!history.length) return 0;

  const targetTime = new Date(timestamp).getTime();
  const first = history[0];
  const latest = history[history.length - 1];

  if (targetTime <= new Date(first.captured_at).getTime()) {
    return Number(first.points || 0);
  }

  if (targetTime >= new Date(latest.captured_at).getTime()) {
    return Number(latest.points || 0);
  }

  for (let index = 1; index < history.length; index++) {
    const previous = history[index - 1];
    const current = history[index];
    const previousTime = new Date(previous.captured_at).getTime();
    const currentTime = new Date(current.captured_at).getTime();

    if (targetTime >= previousTime && targetTime <= currentTime) {
      const span = Math.max(1, currentTime - previousTime);
      const ratio = (targetTime - previousTime) / span;
      return Math.round(Number(previous.points || 0) + (Number(current.points || 0) - Number(previous.points || 0)) * ratio);
    }
  }

  return Number(latest.points || 0);
}

function buildChartRows(history: TrackingSnapshot[], redemptions: Redemption[]) {
  const sorted = [...history]
    .filter(isValidTrackingPoint)
    .sort((a, b) => new Date(a.captured_at).getTime() - new Date(b.captured_at).getTime());
  const rows = sorted.map(snapshot => ({
    capturedAt: snapshot.captured_at,
    capturedTime: new Date(snapshot.captured_at).getTime(),
    chartTime: new Date(snapshot.captured_at).getTime(),
    points: snapshot.points as number | null,
    projectedPoints: null as number | null,
    redemptionPoint: null as number | null,
    redemptions: [] as Redemption[],
    rank: snapshot.rank,
    messagesSent: snapshot.messagesSent
  }));

  if (sorted.length) {
    const firstTime = new Date(sorted[0].captured_at).getTime();
    const latestTime = new Date(sorted[sorted.length - 1].captured_at).getTime();
    const redemptionsByTime = new Map<string, Redemption[]>();

    redemptions.forEach(redemption => {
      const redeemedAt = new Date(redemption.created_at).getTime();
      if (!Number.isFinite(redeemedAt) || redeemedAt < firstTime || redeemedAt > latestTime) {
        return;
      }

      const existing = redemptionsByTime.get(redemption.created_at) || [];
      existing.push(redemption);
      redemptionsByTime.set(redemption.created_at, existing);
    });

    redemptionsByTime.forEach((items, capturedAt) => {
      rows.push({
        capturedAt,
        capturedTime: new Date(capturedAt).getTime(),
        chartTime: new Date(capturedAt).getTime(),
        points: interpolatePointsAt(sorted, capturedAt),
        projectedPoints: null,
        redemptionPoint: interpolatePointsAt(sorted, capturedAt),
        redemptions: items,
        rank: 0,
        messagesSent: 0
      });
    });
  }

  rows.sort((a, b) => new Date(a.capturedAt).getTime() - new Date(b.capturedAt).getTime());

  if (hasThreeDays(sorted)) {
    const latest = sorted[sorted.length - 1];
    const averagePerHour = getAveragePointsPerHour(sorted);

    if (averagePerHour > 0) {
      rows.push({
        capturedAt: new Date(new Date(latest.captured_at).getTime() + DAY_MS).toISOString(),
        capturedTime: new Date(latest.captured_at).getTime() + DAY_MS,
        chartTime: new Date(latest.captured_at).getTime() + DAY_MS,
        points: null,
        projectedPoints: Math.round(latest.points + averagePerHour * 24),
        redemptionPoint: null,
        redemptions: [],
        rank: latest.rank,
        messagesSent: latest.messagesSent
      });
      rows[rows.length - 2].projectedPoints = rows[rows.length - 2].points;
    }
  }

  return rows;
}

function findTooltipRow<T extends { chartTime: number; points: number | null; projectedPoints: number | null }>(
  rows: T[],
  label: unknown,
  payload?: ReadonlyArray<{ payload?: T }>
) {
  const labelTime = Number(label);

  if (Number.isFinite(labelTime)) {
    const exact = rows.find(row => row.chartTime === labelTime);
    if (exact) {
      return exact;
    }

    return rows.reduce<T | null>((closest, row) => {
      if (row.points === null && row.projectedPoints === null) {
        return closest;
      }

      if (!closest) {
        return row;
      }

      return Math.abs(row.chartTime - labelTime) < Math.abs(closest.chartTime - labelTime)
        ? row
        : closest;
    }, null);
  }

  return payload?.find(item => item.payload?.points !== null || item.payload?.projectedPoints !== null)?.payload
    || payload?.[0]?.payload
    || null;
}

function TrackingAreaChart({ rows }: { rows: ReturnType<typeof buildChartRows> }) {
  const realTimelineRows = rows.filter(row => row.points !== null || row.redemptionPoint !== null);
  const firstTime = realTimelineRows.length ? realTimelineRows[0].capturedTime : 0;
  const latestRealTime = realTimelineRows.length ? realTimelineRows[realTimelineRows.length - 1].capturedTime : 0;
  const realTimeSpan = Math.max(1, latestRealTime - firstTime);
  const projectionChartTime = latestRealTime + Math.max(HOUR_MS, Math.min(DAY_MS * 0.12, realTimeSpan * 0.14));
  const chartRows = rows.map(row => (
    row.points === null && row.projectedPoints !== null
      ? { ...row, chartTime: projectionChartTime }
      : row
  ));
  const redemptionRows = chartRows.filter(row => row.redemptions.length && row.redemptionPoint !== null);
  const timelineRows = chartRows.filter(row => row.points !== null || row.projectedPoints !== null || row.redemptionPoint !== null);
  const latestChartTime = timelineRows.length ? timelineRows[timelineRows.length - 1].chartTime : 0;
  const timeSpan = Math.max(1, latestChartTime - firstTime);
  const redemptionMarkers = redemptionRows.map(row => ({
    ...row,
    left: Math.min(100, Math.max(0, ((row.chartTime - firstTime) / timeSpan) * 100))
  }));
  const currentDayMidnight = getCurrentDayMidnightMarker(firstTime, latestRealTime);
  const midnightMarker = currentDayMidnight === null ? null : {
    timestamp: currentDayMidnight
  };

  return (
    <div className="h-[230px] w-full min-w-0 md:h-[260px]">
      <motion.div
        className="relative h-full w-full min-w-0"
        initial={{ clipPath: 'inset(0 100% 0 0)', opacity: 0.96 }}
        animate={{ clipPath: 'inset(0 0% 0 0)', opacity: 1 }}
        transition={{ duration: 1.05, ease: [0.22, 1, 0.36, 1] }}
        style={{ willChange: 'clip-path, opacity' }}
      >
        <div className="pointer-events-none absolute inset-x-0 bottom-3 top-3 z-10">
          {redemptionMarkers.map(row => (
            <div
              key={`redemption-marker-${row.capturedAt}`}
              className="group pointer-events-auto absolute top-0 h-full w-5 -translate-x-1/2 cursor-help"
              style={{ left: `${row.left}%` }}
            >
              <div className="mx-auto h-full w-px bg-amber-300/80 shadow-[0_0_12px_rgba(251,191,36,0.45)]" />
              <div className="absolute left-1/2 top-2 hidden w-52 -translate-x-1/2 rounded-xl border border-amber-300/20 bg-zinc-950/95 p-3 text-xs text-white shadow-xl group-hover:block">
                <div className="mb-2 font-medium text-amber-200">
                  {new Date(row.capturedAt).toLocaleString('hu-HU', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
                <div className="space-y-1">
                  {row.redemptions.map(redemption => (
                    <div key={redemption.id}>{redemption.item_name}</div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
        <ChartContainer config={CHART_CONFIG} className="h-full w-full min-w-0 aspect-auto">
          <ComposedChart accessibilityLayer data={chartRows} margin={{ left: 0, right: 0, top: 10, bottom: 0 }}>
            <defs>
              <linearGradient id="trackingPointsFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ffffff" stopOpacity={0.25} />
                <stop offset="100%" stopColor="#ffffff" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} strokeDasharray="4 4" />
            <XAxis
              dataKey="chartTime"
              type="number"
              domain={['dataMin', 'dataMax']}
              tickLine={false}
              axisLine={false}
              height={8}
              tick={false}
            />
            <ChartTooltip
              filterNull={false}
              content={({ active, payload, label }) => {
                const tooltipRow = findTooltipRow(chartRows, label, payload);
                if (!active || !tooltipRow) return null;
                const row = tooltipRow as typeof rows[number];
                const points = row?.points ?? row?.projectedPoints ?? 0;
                const isProjected = row?.points === null && row?.projectedPoints !== null;

                return (
                  <div className="min-w-[180px] rounded-xl border border-zinc-800 bg-zinc-950/95 p-3 text-xs shadow-xl backdrop-blur-md">
                    <div className="mb-2 font-medium text-zinc-400">
                      {new Date(row.capturedAt).toLocaleString('hu-HU', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                    <div className="mb-2 flex items-baseline gap-2 border-b border-zinc-800 pb-2">
                      <span className="text-xl font-bold text-white">{formatNumber(points)}</span>
                      <span className="text-zinc-500">{isProjected ? 'predicted points' : 'points'}</span>
                    </div>
                    {row?.redemptions.length ? (
                      <div className="space-y-1 border-b border-zinc-800 pb-2 mb-2">
                        <div className="font-medium text-amber-200">Kivaltas</div>
                        {row.redemptions.map(redemption => (
                          <div key={redemption.id} className="text-white/85">{redemption.item_name}</div>
                        ))}
                      </div>
                    ) : null}
                    {!isProjected && (
                      <div className="flex items-center justify-between gap-4 text-zinc-400">
                        <span>Rank</span>
                        <span className="font-medium tabular-nums text-white">#{row?.rank || '-'}</span>
                      </div>
                    )}
                  </div>
                );
              }}
            />
            {midnightMarker && (
              <ReferenceLine
                x={midnightMarker.timestamp}
                stroke="rgba(165, 243, 252, 0.65)"
                strokeDasharray="4 4"
                ifOverflow="extendDomain"
                label={{
                  value: formatMidnightLabel(midnightMarker.timestamp),
                  position: 'insideTop',
                  fill: 'rgba(207, 250, 254, 0.85)',
                  fontSize: 10,
                  dy: 8
                }}
              />
            )}
            {redemptionRows.map(row => (
              <ReferenceLine
                key={`redemption-${row.capturedAt}`}
                x={row.chartTime}
                stroke="rgba(251, 191, 36, 0.55)"
                strokeDasharray="3 4"
                ifOverflow="extendDomain"
              />
            ))}
            <Area
              type="monotone"
              dataKey="points"
              stroke="#ffffff"
              strokeWidth={2}
              fill="url(#trackingPointsFill)"
              activeDot={{ r: 5, stroke: '#ffffff', strokeWidth: 2, fill: '#18181b' }}
              dot={false}
              isAnimationActive={false}
            />
            <Area
              type="monotone"
              dataKey="projectedPoints"
              stroke="#ffffff"
              strokeWidth={2}
              strokeDasharray="5 5"
              fill="transparent"
              activeDot={{ r: 5, stroke: '#ffffff', strokeWidth: 2, fill: '#18181b' }}
              dot={false}
              isAnimationActive={false}
            />
            <Scatter
              data={redemptionRows}
              dataKey="redemptionPoint"
              fill="#fbbf24"
              shape="circle"
              isAnimationActive={false}
            />
          </ComposedChart>
        </ChartContainer>
      </motion.div>
    </div>
  );
}

function ChartPlaceholder() {
  return (
    <div className="flex h-[230px] items-center justify-center rounded-lg border border-white/10 bg-white/[0.04] px-4 text-center text-sm font-medium text-white/50 md:h-[260px] md:px-6">
      Még nincs elég rendelkezésre álló adat. Nézz vissza 1 óra múlva :D
    </div>
  );
}

interface TrackingPanelProps {
  onTrackingUpdate?: (summary: { username: string; points: number | null }) => void;
}

export default function TrackingPanel({ onTrackingUpdate }: TrackingPanelProps) {
  const [usernameInput, setUsernameInput] = useState('');
  const [trackedUsername, setTrackedUsername] = useState('');
  const [history, setHistory] = useState<TrackingHistoryResponse | null>(null);
  const [redemptionCounts, setRedemptionCounts] = useState<UserRedemptionCount[]>([]);
  const [userRedemptions, setUserRedemptions] = useState<Redemption[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingRedemptions, setLoadingRedemptions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [viewMode, setViewMode] = useState<'stats' | 'redemptions'>('stats');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileReady, setTurnstileReady] = useState(false);
  const [showTurnstile, setShowTurnstile] = useState(false);
  const turnstileContainerRef = React.useRef<HTMLDivElement | null>(null);
  const turnstileWidgetIdRef = React.useRef<string | null>(null);
  const pendingUsernameRef = React.useRef('');

  const sanitizedHistory = useMemo(() => (history?.history || []).filter(isValidTrackingPoint), [history]);
  const chartRows = useMemo(() => buildChartRows(sanitizedHistory, userRedemptions), [sanitizedHistory, userRedemptions]);
  const canShowChart = useMemo(() => hasOneHourOfData(sanitizedHistory), [sanitizedHistory]);
  const averagePerHour = useMemo(() => getAveragePointsPerHour(sanitizedHistory), [sanitizedHistory]);
  const noPointsMessage = useMemo(() => getNoPointsMessage(sanitizedHistory), [sanitizedHistory]);
  const predictionPoints = useMemo(() => {
    const value = history?.forecast?.pointsIn24h;
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }, [history]);
  const topPoints = useMemo(() => {
    if (typeof history?.topPoints === 'number') {
      return history.topPoints;
    }

    const points = sanitizedHistory.map(row => Number(row.points || 0));
    return points.length ? Math.max(...points) : 0;
  }, [history, sanitizedHistory]);

  useEffect(() => {
    const savedUsername = getCookie(COOKIE_NAME);
    if (savedUsername) {
      setTrackedUsername(savedUsername);
      setUsernameInput(savedUsername);
    }
  }, []);

  const submitTracking = React.useCallback(async (cleaned: string, token: string) => {
    setSubmitting(true);

    try {
      const data = await startTrackingUser(cleaned, token);
      const username = data.username || cleaned;
      setCookie(COOKIE_NAME, username);
      setTrackedUsername(username);
      setUsernameInput(username);
      setIsEditingUsername(false);
      setShowTurnstile(false);
      setTurnstileToken('');
      setViewMode('stats');
      setHistory(data);
      onTrackingUpdate?.({ username, points: data.latest?.points ?? null });
      toast.success('Tracking elindítva', {
        description: username
      });
    } catch (error) {
      toast.error('Tracking indítása sikertelen', {
        description: error instanceof Error ? error.message : 'Próbáld újra később'
      });
      if (turnstileWidgetIdRef.current && window.turnstile) {
        window.turnstile.reset(turnstileWidgetIdRef.current);
        setTurnstileToken('');
      }
    } finally {
      setSubmitting(false);
    }
  }, [onTrackingUpdate]);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) {
      return;
    }

    if (window.turnstile) {
      setTurnstileReady(true);
      return;
    }

    let active = true;
    const handleLoad = () => {
      if (active) {
        setTurnstileReady(true);
      }
    };
    const handleError = () => {
      if (active) {
        toast.error('Captcha betöltése sikertelen');
      }
    };

    const existingScript = document.querySelector<HTMLScriptElement>('script[data-turnstile-script="true"]');
    if (existingScript) {
      existingScript.addEventListener('load', handleLoad, { once: true });
      existingScript.addEventListener('error', handleError, { once: true });
      return () => {
        active = false;
        existingScript.removeEventListener('load', handleLoad);
        existingScript.removeEventListener('error', handleError);
      };
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.dataset.turnstileScript = 'true';
    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });
    document.head.appendChild(script);

    return () => {
      active = false;
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
    };
  }, []);

  useEffect(() => {
    const shouldShowTurnstile = Boolean(TURNSTILE_SITE_KEY) && showTurnstile && (!trackedUsername || isEditingUsername);

    if (!shouldShowTurnstile || !turnstileReady || !turnstileContainerRef.current || !window.turnstile) {
      return;
    }

    if (turnstileWidgetIdRef.current) {
      return;
    }

    turnstileWidgetIdRef.current = window.turnstile.render(turnstileContainerRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      theme: 'dark',
      callback: (token) => {
        const cleaned = pendingUsernameRef.current || usernameInput.trim().replace(/^@+/, '');
        setTurnstileToken(token);
        if (cleaned) {
          submitTracking(cleaned, token);
        }
      },
      'expired-callback': () => setTurnstileToken(''),
      'error-callback': () => {
        setTurnstileToken('');
        toast.error('Captcha hiba, próbáld újra');
      }
    });

    return () => {
      if (turnstileWidgetIdRef.current && window.turnstile) {
        window.turnstile.remove(turnstileWidgetIdRef.current);
        turnstileWidgetIdRef.current = null;
      }
    };
  }, [trackedUsername, isEditingUsername, turnstileReady, showTurnstile, usernameInput, submitTracking]);

  useEffect(() => {
    if (!trackedUsername) {
      setHistory(null);
      setUserRedemptions([]);
      return;
    }

    let cancelled = false;

    async function loadHistory() {
      setLoadingHistory(true);
      const [data, redemptions] = await Promise.all([
        fetchTrackingHistory(trackedUsername),
        fetchUserRedemptions(trackedUsername)
      ]);
      if (!cancelled) {
        setHistory(data);
        setUserRedemptions(redemptions);
        onTrackingUpdate?.({ username: data.username || trackedUsername, points: data.latest?.points ?? null });
        setLoadingHistory(false);
      }
    }

    loadHistory();
    const timer = window.setInterval(loadHistory, 60 * 1000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [trackedUsername, onTrackingUpdate]);

  useEffect(() => {
    if (viewMode !== 'redemptions' || !trackedUsername) {
      return;
    }

    let cancelled = false;

    async function loadRedemptions() {
      setLoadingRedemptions(true);
      const data = await fetchUserRedemptionCounts(trackedUsername);
      if (!cancelled) {
        setRedemptionCounts(data);
        setLoadingRedemptions(false);
      }
    }

    loadRedemptions();

    return () => {
      cancelled = true;
    };
  }, [viewMode, trackedUsername]);

  async function handleStartTracking() {
    const cleaned = usernameInput.trim().replace(/^@+/, '');
    if (!cleaned) return;

    if (!TURNSTILE_SITE_KEY) {
      toast.error('Captcha nincs beállítva');
      return;
    }

    pendingUsernameRef.current = cleaned;

    if (!showTurnstile) {
      setShowTurnstile(true);
      return;
    }

    if (!turnstileToken) {
      toast.error('Előbb csináld meg a captcha ellenőrzést');
      return;
    }

    await submitTracking(cleaned, turnstileToken);
  }

  const isUsernameFormOpen = !trackedUsername || isEditingUsername;

  return (
    <div className="space-y-4 md:space-y-5">
      {isUsernameFormOpen && (
        <div className="space-y-2 pt-1 text-sm leading-6 text-white/60">
          <p>
            Tracking megkezdése után a rendszer elkezdi nyomon követni a pontszámodat.
            Ezt visszanézheted charton, hogy mikor mennyi pontot kaptál.
          </p>
        </div>
      )}

      <div className={`flex flex-col gap-2 md:flex-row ${trackedUsername && !isEditingUsername ? 'items-end md:justify-end' : ''}`}>
        <div className={trackedUsername && !isEditingUsername ? 'flex justify-end md:flex-none' : 'relative min-w-0 flex-1'}>
          <input
            value={usernameInput}
            aria-label="Kick username"
            onChange={(event) => {
              setUsernameInput(event.target.value);
              setShowTurnstile(false);
              setTurnstileToken('');
              pendingUsernameRef.current = '';
            }}
            placeholder="Kick username"
            disabled={Boolean(trackedUsername) && !isEditingUsername}
            className={`${trackedUsername && !isEditingUsername ? 'hidden' : 'block'} h-11 w-full min-w-0 rounded-full border border-white/10 bg-black/40 px-4 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-white/35 disabled:cursor-default disabled:text-white/70`}
          />
          {trackedUsername && !isEditingUsername && (
            <button
              type="button"
              onClick={() => setIsEditingUsername(true)}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/10 text-white/75 transition hover:bg-white/20 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/25"
              aria-label="Username szerkesztése"
            >
              <Pencil size={14} />
            </button>
          )}
        </div>
        {isUsernameFormOpen && (
          <StatefulButton
            type="button"
            disabled={submitting || !usernameInput.trim()}
            className="h-11 w-full md:w-auto"
            onClick={handleStartTracking}
          >
            Start Track
          </StatefulButton>
        )}
      </div>

      {isUsernameFormOpen && showTurnstile && (
        <div className="min-h-[65px] rounded-xl border border-white/10 bg-white/5 p-3">
          {TURNSTILE_SITE_KEY ? (
            <div ref={turnstileContainerRef} />
          ) : (
            <div className="rounded-lg border border-red-400/20 bg-red-500/10 px-3 py-2 text-sm text-red-200">
              Captcha site key hiányzik.
            </div>
          )}
        </div>
      )}

      {trackedUsername && !isEditingUsername && (
      <Tabs value={viewMode} onValueChange={(value) => setViewMode(value as 'stats' | 'redemptions')} className="gap-4">
        <TabsList className="relative grid h-10 w-full grid-cols-2 rounded-full border border-white/10 bg-white/5 p-1 text-white/70">
        <TabsTrigger
          type="button"
          value="stats"
          className="rounded-full transition-none data-[state=active]:bg-white data-[state=active]:text-black"
        >
          Tracking
        </TabsTrigger>
        <TabsTrigger
          type="button"
          value="redemptions"
          disabled={!trackedUsername}
          className="rounded-full transition-none data-[state=active]:bg-white data-[state=active]:text-black"
        >
          Kiváltások
        </TabsTrigger>
        </TabsList>

        <TabsContents className="min-h-[320px]" transition={{ type: 'spring', stiffness: 260, damping: 28, bounce: 0 }}>

          <TabsContent value="redemptions">
        <div className="min-h-[340px]">
          {loadingRedemptions ? (
            <div className="flex h-[230px] items-center justify-center md:h-[260px]">
              <LoadingIndicator label="Kiváltások betöltése" />
            </div>
          ) : redemptionCounts.length ? (
            <div className="space-y-2">
              {redemptionCounts.map(item => (
                <div
                  key={item.item_name}
                  className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3"
                >
                  <span className="min-w-0 truncate text-sm font-semibold text-white/90">{item.item_name}</span>
                  <span className="shrink-0 rounded-full bg-white/10 px-3 py-1 text-sm font-bold tabular-nums text-white">
                    {item.count}x
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex h-[230px] items-center justify-center rounded-lg border border-white/10 bg-white/5 text-center text-sm text-white/45 md:h-[260px]">
              Nincs kiváltott item ennél a usernél.
            </div>
          )}
        </div>
          </TabsContent>
          <TabsContent value="stats">
        <div className="space-y-4">
          <div className={`grid gap-2 md:gap-3 ${predictionPoints !== null ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-3'}`}>
            <div className="min-w-0 rounded-lg bg-white/5 px-2 py-2.5 md:px-3 md:py-3">
              <div className="text-[10px] uppercase text-white/40 md:text-xs">Jelenlegi</div>
              <div className="mt-1 truncate text-sm font-bold tabular-nums md:text-lg">{formatNumber(history?.latest?.points)}</div>
            </div>
            <div className="min-w-0 rounded-lg bg-white/5 px-2 py-2.5 md:px-3 md:py-3">
              <div className="text-[10px] uppercase text-white/40 md:text-xs">TOP</div>
              <div className="mt-1 truncate text-sm font-bold tabular-nums md:text-lg">{formatNumber(topPoints)}</div>
            </div>
            <div className="min-w-0 rounded-lg bg-white/5 px-2 py-2.5 md:px-3 md:py-3">
              <div className="text-[10px] uppercase text-white/40 md:text-xs">Átlag / óra</div>
              <div className="mt-1 truncate text-sm font-bold tabular-nums md:text-lg">{formatNumber(Math.round(averagePerHour))}</div>
            </div>
            {predictionPoints !== null && (
              <div className="min-w-0 rounded-lg bg-white/5 px-2 py-2.5 md:px-3 md:py-3">
                <div className="text-[10px] uppercase text-white/40 md:text-xs">24h prediction</div>
                <div className="mt-1 truncate text-sm font-bold tabular-nums md:text-lg">{formatNumber(predictionPoints)}</div>
              </div>
            )}
          </div>

          {noPointsMessage && (
            <div className="rounded-lg border border-yellow-400/20 bg-yellow-400/10 px-3 py-2 text-sm font-medium text-yellow-100">
              {noPointsMessage}
            </div>
          )}

          {loadingHistory ? (
            <div className="flex h-[230px] items-center justify-center md:h-[260px]">
              <LoadingIndicator label="Chart betöltése" />
            </div>
          ) : chartRows.length && canShowChart ? (
            <TrackingAreaChart rows={chartRows} />
          ) : (
            <ChartPlaceholder />
          )}
        </div>
          </TabsContent>
        </TabsContents>
      </Tabs>
      )}
    </div>
  );
}
